import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useRouteMatch } from "react-router-dom";
import Authorities from "../../../../auth/authorities";
import withAuthority from "../../../../components/Auth/withAuthority";
import {
  createStyles,
  Grid,
  Hidden,
  makeStyles,
  Theme,
} from "@material-ui/core";
import GoogleMap from "../../../../components/common/googleMap.tsx";
import {
  ERROR_MESSAGE_UNEXPECTED_ERROR,
  SUCCESSFULLY_UPDATED,
} from "../../../../utils/consts";
import PageHeaderMobile from "../../../../components/common/PageHeader/PageHeaderMobile";
import { getObjectDifferences } from "../../../../utils/isDeepEqual";
import {
  getIntegrationConfig,
  getIntegrationsPerLocation,
} from "../../../../services/locationApp/integrationsService";
import DefaultAlert from "../../../../components/alerts/DefaultAlert";
import IntegrationsInfoNode from "./IntegrationsInfoNode";
import {
  createHubriseOAuthState,
  HUBRISE_REDIRECT_PATH,
} from "./hubriseOAuthState";

export interface BasicInfoProps {}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    addressInfoStyle: {
      paddingRight: "12px",
      [theme.breakpoints.down("xs")]: {
        paddingRight: "0px",
      },
    },
    googleMapStyle: {
      paddingLeft: "12px",
      [theme.breakpoints.down("xs")]: {
        paddingLeft: "0px",
        marginTop: "8px",
      },
    },
  }),
);

/* Get all basic information and location information using API call at initial load. */
const Integrations: React.FunctionComponent<BasicInfoProps> = () => {
  const [integrations, setIntegrations] = useState([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [integrationConfig, setIntegrationConfig] = useState("");
  const match: any = useRouteMatch();
  const locationId = (match.params as any).locationId;

  const getAllIntegrationsPerLocation = async () => {
    try {
      const response = await getIntegrationsPerLocation(locationId);
      if (response.status === 200) {
        const data = response.data;
        // Handle response whether it's wrapped in { integrations: [...] } or direct array [...]
        const integrationsList = Array.isArray(data)
          ? data
          : data?.integrations || [];

        setIntegrations(integrationsList);
      }
    } catch (err) {
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
    setIsLoading(false);
  };
  const displayName = localStorage.getItem("businessDisplayName");
  // const autherizationRequest = async (clientId: any) => {
  //   try {
  //     const authUrl = `https://manager.hubrise.com/oauth2/v1/authorize?client_id=${clientId}.clients.hubrise.com&redirect_uri=https://demo.locations.prestoexpress.co.uk/7713dce7-51ec-4870-b251-4bce1c9249f8/presto-locations&response_type=code&scope=location[orders.write,catalog.write]&country=GB&location_name=${displayName}}`;

  //     // Open in new tab
  //     window.open(authUrl, "_blank", "noopener,noreferrer");

  //     // Alternative: You can also use window.open without the second parameter for default new tab behavior
  //     // window.open(authUrl);
  //   } catch (err) {
  //     setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
  //   }
  //   setIsLoading(false);
  // };

  // const getAllIntegrationConfig = async () => {
  //   try {
  //     const response = await getIntegrationConfig(locationId);
  //     if (response.status === 200) {
  //       const data = response.data;
  //       // Handle response whether it's wrapped in { integrations: [...] } or direct array [...]
  //       autherizationRequest(data?.client_id);
  //       setIntegrationConfig(data);
  //     }
  //   } catch (err) {
  //     setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
  //   }
  //   setIsLoading(false);
  // };

  const autherizationRequest = (clientId: any) => {
    try {
      // The config api already returns the client_id with the
      // ".clients.hubrise.com" suffix, so it must not be appended again —
      // doing that produced "...clients.hubrise.com.clients.hubrise.com"
      // and HubRise answered with "Client not found".
      // One fixed redirect URL for every location. The location that starts
      // the connect goes in `state`, with a random value that the redirect
      // page checks on the way back.
      const redirectUri = `${process.env.REACT_APP_PRESTO_LOCATION_DOMAIN}${HUBRISE_REDIRECT_PATH}`;
      const state = createHubriseOAuthState(locationId);
      const authUrl = `https://manager.hubrise.com/oauth2/v1/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(
        redirectUri,
      )}&response_type=code&scope=location[orders.write,catalog.write]&country=GB&location_name=${encodeURIComponent(
        displayName || "",
      )}&state=${encodeURIComponent(state)}`;

      // Navigate the current tab to HubRise instead of opening a new one, so
      // the redirect back from HubRise lands in this same tab.
      window.location.href = authUrl;
    } catch (err) {
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
    // setIsLoading(false) intentionally NOT called here — the caller
    // (getAllIntegrationConfig) owns loading state and sets it exactly once,
    // in its `finally` block, so we don't fire duplicate state updates.
  };

  const getAllIntegrationConfig = async () => {
    try {
      const response = await getIntegrationConfig(locationId);
      if (response.status === 200) {
        const data = response.data;
        autherizationRequest(data?.client_id);
        setIntegrationConfig(data);
      } else {
        // Bug fix: this branch used to close the tab silently, with no
        // feedback to the user at all. Now it surfaces the same error the
        // catch block would.
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      }
    } catch (err) {
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    } finally {
      // Single source of truth for loading state — avoids the double-call
      // that existed when both this function and autherizationRequest each
      // called setIsLoading(false).
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAllIntegrationsPerLocation();
  }, []);

  const classes = useStyles();

  return (
    <>
      <Hidden lgUp>
        <PageHeaderMobile />
      </Hidden>
      <IntegrationsInfoNode
        isLoading={isLoading}
        integrations={integrations}
        getAllIntegrationConfig={getAllIntegrationConfig}
        getAllIntegrationsPerLocation={getAllIntegrationsPerLocation}
      />
      <DefaultAlert
        open={!!error}
        handleClose={() => setError("")}
        message={error}
        severity="error"
      />
    </>
  );
};

export default withAuthority(Integrations, Authorities.BASIC_INFO_READ);
